import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams, useRouteContext, useRouterState } from "@tanstack/react-router";
import { useStatus } from "@powersync/react";
import { PMSyncIndicator, PMBadge, PMIcon, PMAvatar, PMToastViewport } from "@/ds/vendor";
import { PMSidebarModules } from "@/ds/pending/PMSidebarModules";
import { activeModule, sectionFor, useNavCounts, useNavModel, type NavModule, type NavSection } from "./nav";
import { AppCtx, type AppState } from "./AppContext";
import { useNotifications, useTeams } from "@/data/queries";
import { hierarchyFor } from "@/lib/levels";

import { Splash } from "@/ui/Splash";
import { CommandPalette } from "@/ui/CommandPalette";
import { UserMenu } from "@/ui/UserMenu";
import { ToastCtx, useToastQueue } from "@/ui/toast";
import { CelebrationProvider } from "@/ui/celebrate";
import { PlayerProvider, usePlayer } from "@/ui/player";
import { VisitWatcher } from "@/ui/VisitWatcher";

// Breakpoints from preview/guidelines-responsive.html: drawer < 900 ≤ rail < 1200 ≤ full.
function useViewport() {
  const q = () => (innerWidth < 900 ? "drawer" : innerWidth < 1200 ? "rail" : "full");
  const [v, setV] = useState(q);
  useEffect(() => {
    const on = () => setV(q());
    addEventListener("resize", on);
    return () => removeEventListener("resize", on);
  }, []);
  return v;
}

export function Shell() {
  const { teamId } = useParams({ from: "/t/$teamId" });
  const { boot } = useRouteContext({ from: "/t/$teamId" });
  const nav = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const searchNow = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const teams = useTeams();
  const notes = useNotifications();
  const status = useStatus();
  const viewport = useViewport();
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const toasts = useToastQueue();

  const team = teams.data.find((t) => t.id === teamId);
  const hierarchy = hierarchyFor(team?.level_hierarchy);
  const counts = useNavCounts(teamId, boot.userId, hierarchy.levels.at(-1)!.key);
  const sections = useNavModel(hierarchy, boot.userId, counts);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((p) => !p); }
    };
    addEventListener("keydown", on);
    return () => removeEventListener("keydown", on);
  }, []);
  useEffect(() => setDrawer(false), [path]);

  const app: AppState | null = useMemo(
    () => (team ? { mode: boot.mode, userId: boot.userId, userEmail: boot.email, teamId, teamName: team.name, hierarchy, units: team.weather_unit_group === "metric" ? "metric" : "us", signOut: boot.signOut } : null),
    [team, boot, teamId, hierarchy],
  );

  if (!team) {
    if (teams.isLoading || !teams.data.length || (boot.mode === "live" && !status.hasSynced)) return <Splash title="Syncing field data" />;
    return <Splash title="Team not found" detail="This team isn't synced for this account." onRetry={() => nav({ to: "/t" })} />;
  }

  const rest = path.split("/").slice(3).join("/");
  const current = sectionFor(sections, rest);
  const currentModule = activeModule(current, rest, searchNow);
  const goSection = (sec: NavSection) => {
    const first = sec.modules.find((m) => !m.action);
    const target = first ?? { path: sec.path, search: {} as Record<string, string> };
    nav({ to: target.path ? `/t/$teamId/${target.path}` : "/t/$teamId", params: { teamId }, search: target.search } as any);
  };
  const goModule = (sec: NavSection, m: NavModule) => {
    // Actions (Create a task) layer onto the current view when already in the section.
    const search = m.action && current?.id === sec.id ? { ...searchNow, ...m.search } : m.search;
    nav({ to: `/t/$teamId/${m.path}`, params: { teamId }, search } as any);
  };
  const go = (id: string) => nav({ to: id === "home" ? "/t/$teamId" : `/t/$teamId/${id}`, params: { teamId } } as any);
  const unread = notes.data.filter((n) => !n.read_at).length;
  const variant = viewport === "full" && !collapsed ? "full" : "rail";

  const syncState = boot.mode === "demo" ? "synced"
    : !status.connected ? (status.hasSynced ? "offline" : "syncing")
    : status.dataFlowStatus?.uploading ? "uploading" : status.dataFlowStatus?.downloading ? "downloading" : "synced";

  const sidebar = (
    <PMSidebarModules
      variant={viewport === "drawer" ? "full" : variant}
      sections={sections}
      activeSection={current?.id}
      activeModule={currentModule}
      team={null}
      notificationCount={unread}
      collapsible={viewport === "full"}
      onToggleCollapse={() => setCollapsed((c) => !c)}
      onSection={(sec) => goSection(sections.find((x) => x.id === sec.id)!)}
      onModule={(sec, m) => goModule(sections.find((x) => x.id === sec.id)!, sections.find((x) => x.id === sec.id)!.modules.find((x) => x.id === m.id)!)}
      onNotifications={() => go("tasks")}
      onLogout={boot.signOut}
      top={
        <button className={`side-search ${variant === "rail" && viewport !== "drawer" ? "side-search--rail" : ""}`} onClick={() => setPalette(true)} aria-label="Search or ask">
          <PMIcon name="search-outline" size={18} />
          {(variant !== "rail" || viewport === "drawer") && <><span>Search or ask</span><kbd>⌘K</kbd></>}
        </button>
      }
      footer={
        <div className={`side-account ${variant === "rail" && viewport !== "drawer" ? "side-account--rail" : ""}`}>
          <UserMenu teams={teams.data} teamId={teamId} email={boot.email} onSwitch={(id) => nav({ to: "/t/$teamId", params: { teamId: id } })} onSignOut={boot.signOut}>
            <PMAvatar name={boot.email} size={34} />
          </UserMenu>
          {(variant !== "rail" || viewport === "drawer") && (
            <div className="side-account-text">
              <span className="side-account-name">{team.name}</span>
              <span className="side-account-sync"><PMSyncIndicator status={syncState as any} size="sm" />{boot.mode === "demo" && <PMBadge variant="warning" uppercase>Demo</PMBadge>}</span>
            </div>
          )}
        </div>
      }
    />
  );

  return (
    <AppCtx.Provider value={app}>
      <ToastCtx.Provider value={toasts.push}>
      <CelebrationProvider>
      <PlayerProvider onOpenLocation={(id) => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: id } })}>
      <VisitWatcher />
        <PlayerAware className={`shell shell--${viewport}`}>
          {viewport === "drawer" ? (
            drawer && (
              <div className="shell-drawer" role="dialog" aria-label="Navigation" onClick={() => setDrawer(false)}>
                <div onClick={(e) => e.stopPropagation()}>{sidebar}</div>
              </div>
            )
          ) : (
            <div className="shell-side">{sidebar}</div>
          )}
          <div className="shell-main">
            {viewport === "drawer" && (
              <header className="topbar">
                <button className="iconbtn" aria-label="Open navigation" onClick={() => setDrawer(true)}>
                  <PMIcon name="menu" size={22} />
                </button>
                <button className="topbar-search" onClick={() => setPalette(true)} aria-label="Search">
                  <PMIcon name="search-outline" size={18} />
                  <span className="topbar-search-text">Search or ask</span>
                </button>
                <div className="topbar-right">
                  <PMSyncIndicator status={syncState as any} size="sm" />
                </div>
              </header>
            )}
            <main className="shell-content" id="main">
              <Outlet />
            </main>
          </div>
          <CommandPalette open={palette} onClose={() => setPalette(false)} />
          <PMToastViewport toasts={toasts.toasts} onDismiss={toasts.dismiss} placement="bottom-right" />
        </PlayerAware>
      </PlayerProvider>
      </CelebrationProvider>
      </ToastCtx.Provider>
    </AppCtx.Provider>
  );
}

// Adds bottom room for the docked podcast player so it never covers content.
function PlayerAware({ className, children }: { className: string; children: React.ReactNode }) {
  const { current } = usePlayer();
  return <div className={`${className} ${current ? "has-player" : ""}`}>{children}</div>;
}
